import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { getProfilePropertyValuesForPropertyTypeId, getPropertyValuesPassedToService } from "./applied-profile-util";
import { checkIfConfigurationContentIsMultiLanguage, extractContentValuesFromProfileProperties } from "./content-util";

export const getInheritedAndMergedStringPropertyValue = (
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>,
    propertyTypeId: string
): {
    inheritedValue: string;
    mergedValue: string;
} => {
    let propertyValue;

    // get the property from properties list
    const property = properties.find(property =>
        property.propertyTypeId === propertyTypeId &&
        property.addedToService
    );

    if (property) {
        propertyValue = property.value;
    }

    const inheritedValue = getPropertyValuesPassedToService(appliedProfiles, propertyTypeId);

    return {
        inheritedValue,
        mergedValue: typeof propertyValue === "undefined"
            ? inheritedValue
            : propertyValue
    };
};

export const getInheritedAndMergedJSONPropertyValue = <T>(
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>,
    propertyTypeId: string,
    defaultValue: T
): {
    inheritedValue: T;
    mergedValue: T;
} => {
    const profileProperties = getProfilePropertyValuesForPropertyTypeId(appliedProfiles, propertyTypeId);
    const inheritedValue = merge({}, defaultValue, ...profileProperties);

    let mergedValue: T = inheritedValue;
    const property = properties.find(property => property.propertyTypeId === propertyTypeId);
    if (property?.value) {
        try {
            const parsedPropertyValue = JSON.parse(property.value);
            mergedValue = merge({}, inheritedValue, parsedPropertyValue);
        } catch (error) {
            console.error(error);
        }
    }

    return {
        inheritedValue,
        mergedValue
    };
};


export const getInheritedAndMergedContentPropertyValue = <T>(
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>,
    propertyTypeId: string,
    defaultContent: MultiLanguageWrapper<T>,
    selectedLanguage: LocaleTypes
): {
    inheritedContent: T,
    mergedContent: T,
    multiLanguageContent: MultiLanguageWrapper<T>,
    availableLanguages: Array<LocaleTypes>
} => {
    const extractedContent = extractContentValuesFromProfileProperties(
        appliedProfiles,
        propertyTypeId,
        defaultContent,
        selectedLanguage
    );

    let mergedContent = extractedContent.mergedContent;
    let multiLanguageContent = extractedContent.multiLanguageContent;
    const inheritedContent = cloneDeep(mergedContent);

    const contentProperty = properties.find(property => property.propertyTypeId === propertyTypeId);

    if (contentProperty?.value) {
        try {
            const parsedContent = JSON.parse(contentProperty.value);
            const isMultiLanguageFormat = checkIfConfigurationContentIsMultiLanguage(parsedContent);

            if (isMultiLanguageFormat) {
                // store all of the languages that are present in availableLanguages array
                for (const localeTypeValue of Object.values(LocaleTypes)) {
                    const localeType: LocaleTypes = localeTypeValue;
                    if (parsedContent[localeType] && !extractedContent.availableLanguages.includes(localeType)) {
                        extractedContent.availableLanguages.push(localeType);
                    }
                }

                //update the merged content object
                mergedContent = merge({}, mergedContent, (parsedContent as MultiLanguageWrapper<T>)[selectedLanguage]);

                // update multi language content object with new data
                if (multiLanguageContent === null) {
                    multiLanguageContent = merge({}, defaultContent, parsedContent);
                } else {
                    multiLanguageContent = merge({}, multiLanguageContent, parsedContent);
                }
            } else {
                //if its not multilanguage, we know the content is for english, so only
                //merge in the content if the selected language is english
                if (selectedLanguage === LocaleTypes.ENGLISH) {
                    mergedContent = merge({}, mergedContent, parsedContent);
                }

                const partialContent: Partial<MultiLanguageWrapper<T>> = { [LocaleTypes.ENGLISH]: parsedContent };
                // update multi language content object with new data
                if (multiLanguageContent === null) {
                    multiLanguageContent = merge({}, defaultContent, partialContent);
                } else {
                    multiLanguageContent = merge({}, multiLanguageContent, partialContent);
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    return {
        inheritedContent,
        mergedContent,
        multiLanguageContent,
        availableLanguages: extractedContent.availableLanguages
    };
};
